import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { AnalyticsService, LocalService } from '../../../core/services';
import { FormValidation } from '../../../core/utility';

const analyticsService = new AnalyticsService();
const localService = new LocalService();
const formValidation = new FormValidation();

// Campaign Analytics :: add post links through an Excel sheet instead of one by one.
// Download template -> fill -> upload -> valid rows are added to the same "staged" list the single
// Add Post builds, so the brand can still review/delete them before pressing Submit (the existing
// /analytics/post/upload call does the insert and enforces plan limits).

const MAX_ROWS = 500;
const LINK_HEADER = 'Post Link';
// OLD: const INFLUENCER_HEADER = 'Influencer Username (Optional)';
// The second column is platform specific - YouTube creators have a channel handle/name, not a username.
const IG_INFLUENCER_HEADER = 'Influencer Username (Optional)';
const YT_INFLUENCER_HEADER = 'Channel Handle or Name (Optional)';
const influencerHeader = (platform: string) => (platform === 'Instagram' ? IG_INFLUENCER_HEADER : YT_INFLUENCER_HEADER);

const isInstagramPlatform = (platform: string) => (platform || '').toLowerCase() === 'instagram';

const normalizeKey = (k: string) => (k || '').toString().toLowerCase().replace(/[^a-z]/g, '');
const LINK_KEYS = ['postlink', 'posturl', 'link', 'url'];
// OLD: const INFLUENCER_KEYS = ['influencerusernameoptional', 'influencerusername', 'influencer', 'username', 'handle'];
const INFLUENCER_KEYS = [
  'influencerusernameoptional', 'influencerusername', 'influencer', 'username', 'handle',
  'channelhandleornameoptional', 'channelhandleorname', 'channelhandle', 'channelname', 'channel',
];
const normalizeHandle = (v: any) => (v ?? '').toString().trim().toLowerCase().replace(/^@/, '');

export interface ExcelPostLinkRow {
  postLink: string;
  influencer: { sm_id: number | string; name: string };
}

interface Props {
  // OLD: campaignId: number;
  campaignId?: number; // existing campaign - its saved links are skipped. Omit for a new campaign (Track Campaign).
  platform: string; // 'Instagram' | 'Youtube' (case-insensitive)
  // OLD: influencerList: any[];
  influencerList?: any[]; // approved influencers of the campaign (same list as the dropdown)
  withInfluencer?: boolean; // false = template has only the Post Link column (Track Campaign has no influencers). Default true.
  stagedLinks: string[]; // links already in the staged table
  maxRows?: number; // remaining links allowed by the plan (undefined = no limit)
  onLimitReached?: () => void; // shows the existing upgrade popup
  onRows: (rows: ExcelPostLinkRow[]) => void;
  submitLabel?: string; // name of the page's final button, used in the helper text. Default 'Submit'.
}

// OLD: function downloadTemplate(platform: string) {
function downloadTemplate(platform: string, withInfluencer = true) {
  // OLD: const isInstagram = platform === 'Instagram';
  const isInstagram = isInstagramPlatform(platform);
  platform = isInstagram ? 'Instagram' : 'YouTube';
  const INFLUENCER_HEADER = influencerHeader(platform);
  // OLD: const posts = XLSX.utils.aoa_to_sheet([[LINK_HEADER, INFLUENCER_HEADER]]);
  const headers = withInfluencer ? [LINK_HEADER, INFLUENCER_HEADER] : [LINK_HEADER];
  const posts = XLSX.utils.aoa_to_sheet([headers]);
  posts['!cols'] = [{ wch: 60 }, { wch: 32 }];
  const examples = isInstagram
    ? [['https://www.instagram.com/reel/XXXXXXXXXXX/', 'creator_username'], ['https://www.instagram.com/p/XXXXXXXXXXX/', '']]
    : [['https://www.youtube.com/watch?v=XXXXXXXXXXX', '@channelhandle'], ['https://www.youtube.com/shorts/XXXXXXXXXXX', '']];
  const help = XLSX.utils.aoa_to_sheet([
    ['How to fill this sheet'],
    [`1. Add one ${platform} post per row in the "Posts" sheet. Keep the header row as it is.`],
    [`2. "${LINK_HEADER}" is required.`],
    ...(withInfluencer
      ? [[`3. "${INFLUENCER_HEADER}" - the creator's ${isInstagram ? 'Instagram username' : 'YouTube handle or channel name'}. ` +
          'If it matches an approved influencer of this campaign the post is linked to them, otherwise it is added as "Other Influencer".']]
      : []),
    [`${withInfluencer ? 4 : 3}. Up to ${MAX_ROWS} posts per upload. Links already added are skipped automatically.`],
    [],
    ['Example rows (do not paste these into the Posts sheet)'],
    headers,
    ...examples.map((row) => (withInfluencer ? row : row.slice(0, 1))),
  ]);
  help['!cols'] = [{ wch: 100 }, { wch: 32 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, posts, 'Posts');
  XLSX.utils.book_append_sheet(wb, help, 'Instructions');
  XLSX.writeFile(wb, `${platform.toLowerCase()}_post_links_template.xlsx`);
}

// OLD: const ExcelPostLinksUpload: React.FC<Props> = ({ campaignId, platform, influencerList, stagedLinks, maxRows, onLimitReached, onRows }) => {
const ExcelPostLinksUpload: React.FC<Props> = ({ campaignId, platform, influencerList, withInfluencer = true, submitLabel = 'Submit', stagedLinks, maxRows, onLimitReached, onRows }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  // OLD: const isInstagram = platform === 'Instagram';
  const isInstagram = isInstagramPlatform(platform);

  // Approved influencer lookup by any of their handles/names
  const findInfluencer = (value: any): ExcelPostLinkRow['influencer'] | null => {
    const handle = normalizeHandle(value);
    if (!handle) return null;
    const match = (influencerList || []).find((inf: any) =>
      (isInstagram ? [inf.username, inf.insta_name] : [inf.custom_url, inf.title]).some((v) => normalizeHandle(v) === handle)
    );
    if (!match) return null;
    // OLD: return isInstagram ? { sm_id: match.ig_id, name: match.insta_name } : { sm_id: match.yt_id, name: match.title };
    // The backend only links a row to the influencer's proposal when `name` is set (and not "Other Influencer"),
    // so fall back to the handle when the display name is empty.
    return isInstagram
      ? { sm_id: match.ig_id, name: match.insta_name || match.username || `IG ${match.ig_id}` }
      : { sm_id: match.yt_id, name: match.title || match.custom_url || `YT ${match.yt_id}` };
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    setErrors([]);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      setBusy(true);
      try {
        const wb = XLSX.read(ev.target?.result, { type: 'array' });
        const sheet = wb.Sheets['Posts'] || wb.Sheets[wb.SheetNames[0]];
        const data: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        if (data.length > MAX_ROWS) {
          // OLD: localService.toastify(`You can upload at most ${MAX_ROWS} posts at a time.`, 'error', 2000);
          localService.toastify(`This file has ${data.length} rows - the limit is ${MAX_ROWS} posts per file. Please split it into multiple files.`, 'error', 4000);
          return;
        }

        // links already saved on the campaign (ignore blank rows - ''.includes is always true)
        // OLD: const res: any = await analyticsService.getAnalyticsLinks(campaignId);
        const res: any = campaignId ? await analyticsService.getAnalyticsLinks(campaignId) : null;
        const saved: string[] = (res?.data?.rows || []).map((r: any) => (r?.post_url || '').trim()).filter(Boolean);
        const staged = (stagedLinks || []).map((l) => (l || '').trim()).filter(Boolean);
        const clash = (u: string, list: string[]) => list.some((l) => l.includes(u) || u.includes(l));

        const rows: ExcelPostLinkRow[] = [];
        const rowErrors: string[] = [];
        const seen: string[] = [];
        let unmatched = 0;
        data.forEach((raw, i) => {
          const rowNo = i + 2; // header is row 1
          const entries = Object.entries(raw || {});
          const rawLink = (entries.find(([k]) => LINK_KEYS.includes(normalizeKey(k)))?.[1] ?? '').toString().trim();
          const rawInfluencer = withInfluencer ? entries.find(([k]) => INFLUENCER_KEYS.includes(normalizeKey(k)))?.[1] : undefined;
          if (!rawLink) return; // blank row

          // same checks as the single Add Post (+ instagram.com/<username>/reel/<code> -> canonical)
          const postLink = isInstagram ? formValidation.normalizeInstagramPostUrl(rawLink) : rawLink;
          const check = isInstagram ? formValidation.CheckInstagramUrl(postLink) : formValidation.CheckYouTubeUrl(postLink);
          if (!check.status) return rowErrors.push(`Row ${rowNo}: ${check.message} - ${rawLink}`);
          if (clash(postLink, seen)) return rowErrors.push(`Row ${rowNo}: repeated in file - ${rawLink}`);
          if (clash(postLink, saved)) return rowErrors.push(`Row ${rowNo}: already added to this campaign - ${rawLink}`);
          if (clash(postLink, staged)) return rowErrors.push(`Row ${rowNo}: already in the list below - ${rawLink}`);
          seen.push(postLink);

          const influencer = findInfluencer(rawInfluencer);
          if (!influencer && normalizeHandle(rawInfluencer)) unmatched++;
          rows.push({ postLink, influencer: influencer || { sm_id: 0, name: 'Other Influencer' } });
        });

        if (rows.length === 0 && rowErrors.length === 0) {
          localService.toastify(`No post links found. Fill the "${LINK_HEADER}" column of the template.`, 'error', 2000);
          return;
        }

        // plan limit (Basic plans) - add what fits, then show the existing upgrade popup
        let accepted = rows;
        if (maxRows !== undefined && rows.length > maxRows) {
          accepted = rows.slice(0, Math.max(maxRows, 0));
          rowErrors.push(`${rows.length - accepted.length} link(s) not added - your plan's post link limit was reached.`);
          onLimitReached?.();
        }

        if (accepted.length > 0) {
          onRows(accepted);
          localService.toastify(
            `${accepted.length} link${accepted.length > 1 ? 's' : ''} added to the list. Review and press ${submitLabel}.` +
              (unmatched ? ` ${unmatched} influencer name(s) not found - added as Other Influencer.` : ''),
            'success',
            3000
          );
        }
        setErrors(rowErrors);
      } catch (err) {
        localService.toastify('Could not read this file. Please use the downloaded template (.xlsx).', 'error', 2000);
      } finally {
        setBusy(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="excel-post-links-upload mb-3">
      <div className="d-flex align-items-center gap-2 my-2" style={{ color: '#64748b', fontSize: 14 }}>
        <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
        or add many posts with Excel
        <span style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
      </div>
      <div className="d-flex flex-wrap gap-2">
        <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center" onClick={() => downloadTemplate(platform, withInfluencer)}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 18 }}>download</span>
          Download Template
        </button>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm d-flex align-items-center"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          <span className="material-symbols-rounded me-1" style={{ fontSize: 18 }}>upload</span>
          {busy ? 'Reading file...' : 'Upload Excel'}
        </button>
        <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleFile} />
      </div>
      <div className="mt-1" style={{ color: '#64748b', fontSize: 12 }}>
        Up to <strong style={{ color: '#334155', fontWeight: 600 }}>{MAX_ROWS} posts per file</strong>. Have more? Upload another
        file before pressing {submitLabel} - it adds to the same list.
      </div>
      {errors.length > 0 && (
        <div className="mt-2 p-2 rounded" style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 12 }}>
          <div className="fw-semibold mb-1">{errors.length} row(s) skipped:</div>
          <ul className="mb-0 ps-3" style={{ maxHeight: 120, overflowY: 'auto', wordBreak: 'break-all' }}>
            {errors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ExcelPostLinksUpload;
